import { createElement, CSSProperties, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

export type TextProps = {
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "p" | "span" | "li";
  type?:
    | "headline1"
    | "headline2"
    | "subtitle1"
    | "subtitle2"
    | "body1"
    | "body2"
    | "body3"
    | "caption"
    | "inherit";
  weight?: "100" | "200" | "300" | "400" | "500" | "600" | "700" | "inherit";
  color?:
    | "primary"
    | "secondary"
    | "success"
    | "error"
    | "warning"
    | "inherit"
    | "white";
  bold?: boolean;
  className?: string;
  firstCapitalize?: boolean;
  children?: ReactNode;
  style?: CSSProperties;
  medium?: boolean;
  inherit?: boolean;
  html?: string;
};

const types: Record<Required<TextProps>["type"], string> = {
  body1: "text-sm xl:text-base",
  body2: "text-sm",
  body3: "text-xs",
  caption: "text-xs",
  headline1: "text-lg md:text-2xl",
  headline2: "text-xl",
  subtitle1: "text-base",
  subtitle2: "text-sm",
  inherit: "", // TODO make it better
};

const weights: Record<Required<TextProps>["weight"], string> = {
  "100": "font-thin",
  "200": "font-extralight",
  "300": "font-light",
  "400": "font-normal",
  "500": "font-medium",
  "600": "font-semibold",
  "700": "font-bold",
  inherit: "font-inherit",
};

const colors: Record<Required<TextProps>["color"], string> = {
  primary: "#05213B",
  secondary: "#697886",
  error: "#E94F54",
  success: "#2D912D",
  warning: "#B68C14",
  inherit: "inherit",
  white: "white",
};

export default function Text({
  as = "p",
  type = "body1",
  weight = "400",
  bold,
  children,
  color = "primary",
  className,
  firstCapitalize,
  style,
  medium,
  inherit,
  ...rest
}: TextProps) {
  const dangerouslySetInnerHTML = rest.html ? { __html: rest.html } : undefined;

  return createElement(
    as,
    {
      dangerouslySetInnerHTML,
      style: {
        color: inherit ? colors.inherit : colors[color],

        ...style,
      },
      className: twMerge(
        types[type],
        weights[weight],

        firstCapitalize && "first-letter:uppercase",
        medium && "font-medium",
        inherit && twMerge(types.inherit, weights.inherit),

        as === "h1" && "font-medium text-lg xl:text-2xl",
        as === "h2" && "font-medium text-base xl:text-lg",
        as === "h3" && "font-medium text-base",
        as === "h4" && "font-medium text-sm",

        bold ? "font-bold" : "",

        className,
      ),
      ...rest,
    },
    children,
  );
}
